import axios from "axios";

interface GraphUserInfo {
  "@odata.context": string;
  businessPhones?: string[];
  displayName: string;
  givenName?: string;
  id: string;
  jobTitle?: string | null;
  mail?: string;
  mobilePhone: string | null;
  officeLocation: string | null;
  preferredLanguage: string | null;
  surname?: string;
  userPrincipalName?: string;
  UserId: number;
  avatar: string;
  authority?: number | boolean;
  pendingAdmit: Boolean;
  userName: string;
  userId: number;
}

interface TokenResult {
  access_token: string;
  expires_in: number;
  ext_expires_in: number;
  expire_date: number;
  scope: string;
  token_type: string;
}

export interface UserInfo {
  wecom_userid: string;
  graphUserInfo?: GraphUserInfo;
  tokenResult?: TokenResult;
}

// 内存中存储用户信息
let userInfoCache: UserInfo | null = null;

// 从 cookie 中获取指定名称的值
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// 设置 cookie
function setCookie(name: string, value: string, isSession: boolean = true): void {
  if (isSession) {
    // 会话 cookie，关闭浏览器标签后失效
    document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
  } else {
    // 持久化 cookie
    const expires = new Date();
    expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000); // 1天
    document.cookie = `${name}=${value}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  }
}

// 删除 cookie
function deleteCookie(name: string): void {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// 从 cookie 获取 access_token
export function getAccessToken(): string | null {
  return getCookie("access_token");
}

// 从内存获取用户信息
export function getUserInfo(): UserInfo | null {
  return userInfoCache;
}

// 从 API 获取用户信息并缓存到内存
export async function fetchUserInfo(): Promise<UserInfo | null> {
  try {
    const accessToken = getAccessToken();
    
    if (!accessToken) {
      console.error("No access_token found in cookie");
      return null;
    }

    const response = await axios.get(
      `/api/User/GetMyInformation`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.data && response.data.Data) {
      const apiData = response.data.Data; // API 返回的 UserEntity
      console.log("API 返回的原始数据:", apiData);
      
      // 将 UserEntity 转换为前端期望的 UserInfo 格式
      const userInfo: UserInfo = {
        wecom_userid: apiData.WeComId || '',
        graphUserInfo: {
          "@odata.context": '',
          displayName: apiData.UserName || apiData.EnName || '',
          id: apiData.MudId || '',
          UserId: apiData.UserId,
          userName: apiData.UserName || '',
          userId: apiData.UserId,
          avatar: apiData.Avatar || '',
          authority: apiData.Authority,
          pendingAdmit: apiData.PendingAdmit,
          mobilePhone: null,
          officeLocation: null,
          preferredLanguage: null,
          jobTitle: apiData.JobTitle || null,
          mail: apiData.Email || '',
        },
        tokenResult: {
          access_token: accessToken,
          expires_in: 3600,
          ext_expires_in: 3600,
          expire_date: new Date().getTime() + 3600 * 1000, // 默认1小时后过期
          scope: '',
          token_type: 'Bearer'
        }
      };
      
      userInfoCache = userInfo;
      console.log("转换后的 userInfo:", userInfoCache);
      return userInfoCache;
    }
    
    return null;
  } catch (error) {
    console.error("Failed to get user information:", error);
    return null;
  }
}

// 保存用户信息到内存和 cookie
export function saveUserInfo(userInfo: UserInfo | null): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (userInfo) {
        if (userInfo.graphUserInfo) {
          // 精简存储字段
          delete userInfo.graphUserInfo.businessPhones;
          delete userInfo.graphUserInfo.givenName;
          delete userInfo.graphUserInfo.jobTitle;
          delete userInfo.graphUserInfo.mail;
          delete userInfo.graphUserInfo.surname;
          delete userInfo.graphUserInfo.userPrincipalName;
          delete userInfo.graphUserInfo.authority;
        }
        if (userInfo.tokenResult && userInfo.tokenResult.expires_in) {
          const expiresInMilliseconds = userInfo.tokenResult.expires_in * 1000;
          const expirationDate = new Date().getTime() + expiresInMilliseconds;
          userInfo.tokenResult.expire_date = expirationDate;
        }
        
        // 将 access_token 存入会话 cookie
        if (userInfo.tokenResult?.access_token) {
          setCookie("access_token", userInfo.tokenResult.access_token, true);
        }
        
        // 将完整用户信息存入内存
        userInfoCache = userInfo;
      } else {
        // 清空内存和 cookie
        userInfoCache = null;
        deleteCookie("access_token");
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

// 清空用户信息
export function clearUserInfo(): void {
  userInfoCache = null;
  deleteCookie("access_token");
}
